import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, EmptyState } from "@/components/Layout";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Button, ErrorNote, inputClass, useCanWrite } from "@/components/ui";
import { ApiError, api, errorMessage } from "@/lib/api";
import { lots } from "@/lib/format";
import { useRefreshDirectory } from "@/lib/queries";
import type { Closure, DeletedRoots } from "@/lib/types";

/**
 * GLI ELIMINATI: cliente, conto, ciclo e fase non si cancellano, si timbrano —
 * e da qui tornano, se non violano un unico parziale (LOGIN_TAKEN, SEQ_TAKEN,
 * EMAIL_TAKEN) e se il loro cliente non è eliminato (CLIENT_DELETED).
 */

type Kind = "clients" | "accounts" | "cycles" | "sessions";

type Ruolo = "PROP" | "BROKER" | "NONE";

interface Row {
  id: string;
  kind: Kind;
  tipo: string;
  nome: string;
  /** Solo le righe Cliente: la mail sta sotto il nome, in piccolo. */
  mail: string | null;
  mt5Login: string | null;
  server: string | null;
  /** Prop firm o broker proprietario del server (o del ciclo): serve al filtro. */
  firm: string | null;
  ruolo: Ruolo | null;
  clienteId: string;
  cliente: string;
  deletedAt: string;
  chi: string | null;
  closures: Closure[] | null;
}

function rowsFrom(data: DeletedRoots): Row[] {
  return [
    ...data.clients.map((c) => ({
      id: c.id,
      kind: "clients" as const,
      tipo: "Cliente",
      nome: c.name,
      mail: c.email,
      mt5Login: null,
      server: null,
      firm: null,
      ruolo: null,
      clienteId: c.id,
      cliente: c.name,
      deletedAt: c.deletedAt,
      chi: c.deletedBy,
      closures: c.closures,
    })),
    ...data.accounts.map((a) => ({
      id: a.id,
      kind: "accounts" as const,
      tipo: "Conto",
      nome: a.label,
      mail: null,
      mt5Login: a.mt5Login,
      server: a.server.name,
      firm: a.server.propFirm?.name ?? null,
      ruolo: a.role,
      clienteId: a.client.id,
      cliente: a.client.name,
      deletedAt: a.deletedAt,
      chi: a.deletedBy,
      closures: a.closures,
    })),
    ...data.cycles.map((c) => ({
      id: c.id,
      kind: "cycles" as const,
      tipo: "Ciclo",
      nome: c.label ?? "—",
      mail: null,
      mt5Login: null,
      server: null,
      firm: c.propFirm.name,
      ruolo: null,
      clienteId: c.client.id,
      cliente: c.client.name,
      deletedAt: c.deletedAt,
      chi: c.deletedBy,
      closures: c.closures,
    })),
    // Di una Fase si mostra il conto PROP (la gamba di partenza): numero, server e ruolo sono i suoi.
    ...data.sessions.map((f) => ({
      id: f.id,
      kind: "sessions" as const,
      tipo: "Fase",
      nome: `Fase ${f.phase} · ${f.canonicalSymbol}`,
      mail: null,
      mt5Login: f.propAccount.mt5Login,
      server: f.propAccount.server.name,
      firm: f.propAccount.server.propFirm?.name ?? null,
      ruolo: "PROP" as const,
      clienteId: f.client.id,
      cliente: f.client.name,
      deletedAt: f.deletedAt,
      chi: f.deletedBy,
      closures: f.closures,
    })),
  ];
}

/*
 * DATE SUL FUSO DI ROMA, mostrate e filtrate.
 *
 * Il browser userebbe il fuso di chi guarda; il giorno di un'eliminazione e'
 * quello italiano (come per le note). «Il giorno» e' una stringa AAAA-MM-GG,
 * confrontabile con quella degli input `date` senza fare conti sugli istanti.
 */
const ROMA = "Europe/Rome";
const giornoRoma = (iso: string): string => new Intl.DateTimeFormat("en-CA", { timeZone: ROMA, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
const quandoRoma = (iso: string): string =>
  new Date(iso).toLocaleString("it-IT", { timeZone: ROMA, day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const RUOLO_LABEL: Record<Ruolo, string> = { PROP: "Prop", BROKER: "Broker", NONE: "Nessun ruolo" };

/** Esito delle chiusure a mercato. Rosso se qualcosa è rimasto aperto; nullo = non registrato, non «nessuna». */
function EsitoChiusure({ closures }: { closures: Closure[] | null }) {
  if (closures === null) return <span className="text-xs text-faint">chiusure: non registrate</span>;
  if (!closures.length) return <span className="text-xs text-muted">nessuna posizione a mercato</span>;
  const rimaste = closures.filter((c) => c.esito === "RIMASTA_APERTA");
  if (!rimaste.length) return <span className="text-xs text-muted">{closures.length === 1 ? "1 posizione chiusa" : `${closures.length} posizioni chiuse`}</span>;
  return (
    <span className="text-xs text-loss">
      {rimaste.length === 1 ? "1 posizione rimasta aperta" : `${rimaste.length} posizioni rimaste aperte`}:{" "}
      {rimaste.map((c) => `${c.accountLabel} ${c.symbol} ${lots(c.lots)} lotti, ticket ${c.ticket}${c.motivo ? ` (${c.motivo})` : ""}`).join("; ")}
    </span>
  );
}

/** Che cosa ha detto l'ultimo ripristino riuscito: chi è rimasto fuori, quali Fasi aspettano il broker. */
interface EsitoRipristino {
  nome: string;
  messages: string[];
}

/**
 * Il pulsante ripristina, col messaggio del backend in caso di rifiuto (LOGIN_TAKEN, SEQ_TAKEN).
 * Sul conto broker col numero ripreso, quando si era portato via Fasi e cicli, il rifiuto offre
 * «Ripristina senza il broker»: tornano loro, lui resta qui (12/10).
 */
function Ripristina({ row, onRestored }: { row: Row; onRestored: (e: EsitoRipristino) => void }) {
  const refresh = useRefreshDirectory();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [senzaBroker, setSenzaBroker] = useState(false);

  const go = async (opts: { senzaBroker?: boolean } = {}) => {
    setBusy(true);
    setError(null);
    setSenzaBroker(false);
    try {
      const r = await api.undelete(row.kind, row.id, opts);
      // La riga sparisce dall'elenco col ricaricamento: l'esito si mostra sopra la tabella.
      onRestored({ nome: `${row.tipo} ${row.nome}`, messages: r.messages ?? [] });
      await Promise.all([qc.invalidateQueries({ queryKey: ["deleted"] }), refresh()]);
    } catch (e) {
      setError(errorMessage(e));
      setSenzaBroker(e instanceof ApiError && e.canRestoreWithoutBroker);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => void go()}>
        {busy ? "Ripristino…" : "Ripristina"}
      </Button>
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
      {senzaBroker && (
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void go({ senzaBroker: true })}>
          Ripristina senza il broker
        </Button>
      )}
    </span>
  );
}

/** L'esito dell'ultimo ripristino: quello che è tornato, e soprattutto quello che NON è tornato. */
function EsitoBanner({ esito, onClose }: { esito: EsitoRipristino; onClose: () => void }) {
  return (
    <div role="status" className={`rounded border px-4 py-3 text-sm ${esito.messages.length ? "border-warn/60 bg-warn/10" : "border-line bg-raised/50"}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-ink">
          {esito.nome} ripristinato{esito.messages.length ? ", con queste eccezioni:" : ": è tornato tutto."}
        </p>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Chiudi
        </Button>
      </div>
      {esito.messages.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-muted">
          {esito.messages.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const KIND_LABEL: Record<"TUTTI" | Kind, string> = {
  TUTTI: "Tutti",
  clients: "Cliente",
  accounts: "Conto",
  cycles: "Ciclo",
  sessions: "Fase",
};

export function Deleted() {
  const canWrite = useCanWrite();
  const q = useQuery({ queryKey: ["deleted"], queryFn: () => api.deleted() });
  const [kind, setKind] = useState<"TUTTI" | Kind>("TUTTI");
  // `?q=` precompila la ricerca: e' il link «Apri» del messaggio di conto duplicato.
  const [params] = useSearchParams();
  const [cerca, setCerca] = useState(params.get("q") ?? "");
  const [clienteId, setClienteId] = useState("");
  const [ruolo, setRuolo] = useState<"" | Ruolo>("");
  const [possessore, setPossessore] = useState(""); // "f:<prop firm/broker>" oppure "s:<server>"
  const [dal, setDal] = useState("");
  const [al, setAl] = useState("");
  const [esito, setEsito] = useState<EsitoRipristino | null>(null);

  const header = <PageHeader title="Eliminati" subtitle="Clienti, conti, cicli e fasi eliminati: da qui tornano, se non violano un vincolo" />;

  if (q.isLoading) {
    return (
      <>
        {header}
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      </>
    );
  }
  if (q.error) {
    return (
      <>
        {header}
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(q.error)}</ErrorNote>
        </div>
      </>
    );
  }

  const rows = q.data ? rowsFrom(q.data) : [];
  const counts: Record<"TUTTI" | Kind, number> = { TUTTI: rows.length, clients: 0, accounts: 0, cycles: 0, sessions: 0 };
  for (const r of rows) counts[r.kind]++;

  // Le opzioni dei filtri vengono dai dati gia' caricati: niente chiamate in piu'.
  const clienti = [...new Map(rows.map((r) => [r.clienteId, r.cliente])).entries()].sort((x, y) => x[1].localeCompare(y[1], "it"));
  const firms = [...new Set(rows.map((r) => r.firm).filter((x): x is string => !!x))].sort((x, y) => x.localeCompare(y, "it"));
  const servers = [...new Set(rows.map((r) => r.server).filter((x): x is string => !!x))].sort((x, y) => x.localeCompare(y, "it"));
  const ago = cerca.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (kind !== "TUTTI" && r.kind !== kind) return false;
    if (clienteId && r.clienteId !== clienteId) return false;
    if (ruolo && r.ruolo !== ruolo) return false;
    if (possessore.startsWith("f:") && r.firm !== possessore.slice(2)) return false;
    if (possessore.startsWith("s:") && r.server !== possessore.slice(2)) return false;
    const giorno = giornoRoma(r.deletedAt);
    if (dal && giorno < dal) return false;
    if (al && giorno > al) return false;
    if (ago && !(r.mt5Login ?? "").toLowerCase().includes(ago) && !r.nome.toLowerCase().includes(ago)) return false;
    return true;
  });
  const filtriAttivi = !!(ago || clienteId || ruolo || possessore || dal || al);
  const azzera = () => {
    setCerca("");
    setClienteId("");
    setRuolo("");
    setPossessore("");
    setDal("");
    setAl("");
  };

  const pill = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
      active ? "border-accent text-ink" : "border-line text-muted hover:text-ink"
    }`;

  const columns: DataTableColumn<Row>[] = [
    { key: "tipo", header: "Tipo", cell: (r) => r.tipo, sortBy: (r) => r.tipo },
    {
      key: "nome",
      header: "Nome / etichetta",
      cell: (r) => (
        <span className="inline-flex flex-col">
          <span>{r.nome}</span>
          {r.mail && <span className="text-xs text-muted">{r.mail}</span>}
        </span>
      ),
      sortBy: (r) => r.nome,
    },
    { key: "mt5Login", header: "Numero MT5", cell: (r) => (r.mt5Login ? <span className="font-mono">{r.mt5Login}</span> : "—"), sortBy: (r) => r.mt5Login ?? "" },
    {
      key: "server",
      header: "Server",
      cell: (r) =>
        r.server || r.firm ? (
          <span className="inline-flex flex-col">
            {r.server && <span>{r.server}</span>}
            {r.firm && <span className="text-xs text-muted">{r.firm}</span>}
          </span>
        ) : (
          "—"
        ),
      sortBy: (r) => r.server ?? r.firm ?? "",
    },
    {
      key: "ruolo",
      header: "Ruolo",
      cell: (r) =>
        r.ruolo ? <span className={r.ruolo === "PROP" ? "text-prop" : r.ruolo === "BROKER" ? "text-broker" : "text-muted"}>{RUOLO_LABEL[r.ruolo]}</span> : "—",
      sortBy: (r) => r.ruolo ?? "",
    },
    { key: "cliente", header: "Cliente", cell: (r) => r.cliente, sortBy: (r) => r.cliente },
    { key: "deletedAt", header: "Eliminato il", cell: (r) => quandoRoma(r.deletedAt), sortBy: (r) => r.deletedAt },
    { key: "chi", header: "Da chi", cell: (r) => r.chi ?? "utente non più leggibile" },
    { key: "esito", header: "Posizioni", cell: (r) => <EsitoChiusure closures={r.closures} /> },
    ...(canWrite ? [{ key: "azioni", header: "Azione", align: "right" as const, cell: (r: Row) => <Ripristina row={r} onRestored={setEsito} /> }] : []),
  ];

  return (
    <>
      {header}
      <div className="space-y-4 px-6 py-5">
        {esito && <EsitoBanner esito={esito} onClose={() => setEsito(null)} />}
        <div className="flex flex-wrap items-center gap-2">
          {(["TUTTI", "clients", "accounts", "cycles", "sessions"] as const).map((k) => (
            <button key={k} onClick={() => setKind(k)} className={pill(kind === k)}>
              {KIND_LABEL[k]} <span className="text-faint">{counts[k]}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted">
            Cerca numero o etichetta
            <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="es. 1234567" className={`${inputClass} w-48`} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Cliente
            <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} className={`${inputClass} w-48`}>
              <option value="">Tutti</option>
              {clienti.map(([id, nome]) => (
                <option key={id} value={id}>
                  {nome}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Ruolo
            <select value={ruolo} onChange={(e) => setRuolo(e.target.value as "" | Ruolo)} className={`${inputClass} w-32`}>
              <option value="">Tutti</option>
              <option value="PROP">Prop</option>
              <option value="BROKER">Broker</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Prop firm / server
            <select value={possessore} onChange={(e) => setPossessore(e.target.value)} className={`${inputClass} w-56`}>
              <option value="">Tutti</option>
              {firms.length > 0 && (
                <optgroup label="Prop firm / broker">
                  {firms.map((f) => (
                    <option key={`f:${f}`} value={`f:${f}`}>
                      {f}
                    </option>
                  ))}
                </optgroup>
              )}
              {servers.length > 0 && (
                <optgroup label="Server">
                  {servers.map((sv) => (
                    <option key={`s:${sv}`} value={`s:${sv}`}>
                      {sv}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Eliminato dal
            <input type="date" value={dal} max={al || undefined} onChange={(e) => setDal(e.target.value)} className={`${inputClass} w-40`} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            al
            <input type="date" value={al} min={dal || undefined} onChange={(e) => setAl(e.target.value)} className={`${inputClass} w-40`} />
          </label>
          {filtriAttivi && (
            <Button size="sm" variant="ghost" onClick={azzera}>
              Azzera filtri
            </Button>
          )}
        </div>
        <p className="text-xs text-muted">Date nel fuso di Roma.{filtriAttivi ? ` ${filtered.length} di ${rows.length} righe.` : ""}</p>
        <div className="card">
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            initialSort={{ key: "deletedAt", dir: "desc" }}
            empty={
              filtriAttivi && rows.length > 0
                ? { title: "Nessuna riga con questi filtri", hint: "Allarga le date o azzera i filtri." }
                : { title: "Niente di eliminato", hint: "Clienti, conti, cicli e fasi eliminati compaiono qui, con la possibilità di tornare indietro." }
            }
          />
        </div>
      </div>
    </>
  );
}
